import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  opacity: number;
  opacityDir: number;
  life: number;
  maxLife: number;
}

const GOLD_R = 201;
const GOLD_G = 168;
const GOLD_B = 76;
const PARTICLE_COUNT = 72;
const CONNECTION_DIST = 130;
const MAX_LINE_OPACITY = 0.13;

function createParticle(W: number, H: number, randomY = true): Particle {
  const maxLife = 6000 + Math.random() * 8000;
  return {
    x: Math.random() * W,
    y: randomY ? Math.random() * H : H + 10,
    vx: (Math.random() - 0.5) * 0.25,
    vy: -(0.12 + Math.random() * 0.28),
    radius: 0.6 + Math.random() * 1.4,
    opacity: 0,
    opacityDir: 1,
    life: 0,
    maxLife,
  };
}

export default function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let W = 0;
    let H = 0;
    let particles: Particle[] = [];

    const resize = () => {
      W = canvas.offsetWidth;
      H = canvas.offsetHeight;
      canvas.width = W;
      canvas.height = H;
    };

    resize();
    window.addEventListener('resize', resize);

    // Seed initial particles spread across the canvas
    particles = Array.from({ length: PARTICLE_COUNT }, () => createParticle(W, H, true));

    // A few horizontal drifting "light ray" lines
    interface Ray {
      y: number;
      vy: number;
      opacity: number;
      width: number;
      life: number;
      maxLife: number;
    }

    const createRay = (): Ray => ({
      y: Math.random() * H,
      vy: -(0.04 + Math.random() * 0.06),
      opacity: 0,
      width: 80 + Math.random() * 200,
      life: 0,
      maxLife: 10000 + Math.random() * 12000,
    });

    let rays: Ray[] = Array.from({ length: 4 }, () => {
      const r = createRay();
      r.life = Math.random() * r.maxLife; // stagger start
      return r;
    });

    const FADE_DURATION = 1500;

    const draw = (dt: number) => {
      ctx.clearRect(0, 0, W, H);

      // --- Rays ---
      rays.forEach((ray, i) => {
        ray.life += dt;
        ray.y += ray.vy * (dt / 16);

        const progress = ray.life / ray.maxLife;
        let alpha = 0;
        if (progress < 0.15) alpha = (progress / 0.15) * 0.07;
        else if (progress < 0.75) alpha = 0.07;
        else alpha = ((1 - progress) / 0.25) * 0.07;

        if (ray.life >= ray.maxLife) {
          rays[i] = createRay();
          rays[i].y = H + 20;
        }

        if (alpha > 0) {
          const grd = ctx.createLinearGradient(0, ray.y, ray.width, ray.y);
          grd.addColorStop(0, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},0)`);
          grd.addColorStop(0.3, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${alpha})`);
          grd.addColorStop(0.7, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${alpha})`);
          grd.addColorStop(1, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},0)`);
          ctx.fillStyle = grd;
          ctx.fillRect(0, ray.y - 0.5, ray.width, 1);
        }
      });

      // --- Particle connections ---
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const t = 1 - dist / CONNECTION_DIST;
            const alpha = t * t * MAX_LINE_OPACITY * Math.min(particles[i].opacity, particles[j].opacity);
            if (alpha > 0.005) {
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${alpha})`;
              ctx.lineWidth = 0.5;
              ctx.stroke();
            }
          }
        }
      }

      // --- Particles ---
      particles.forEach((p, i) => {
        p.life += dt;
        p.x += p.vx * (dt / 16);
        p.y += p.vy * (dt / 16);

        // Fade in / out
        const fadeRatio = FADE_DURATION / p.maxLife;
        const progress = p.life / p.maxLife;
        if (progress < fadeRatio) {
          p.opacity = (progress / fadeRatio) * 0.85;
        } else if (progress > 1 - fadeRatio) {
          p.opacity = ((1 - progress) / fadeRatio) * 0.85;
        } else {
          // Gentle pulse
          p.opacity += p.opacityDir * 0.003;
          if (p.opacity >= 0.85) p.opacityDir = -1;
          if (p.opacity <= 0.35) p.opacityDir = 1;
        }

        // Recycle
        if (p.life >= p.maxLife || p.y < -20 || p.x < -20 || p.x > W + 20) {
          particles[i] = createParticle(W, H, false);
          return;
        }

        // Draw glow
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.radius * 3.5);
        glow.addColorStop(0, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${p.opacity * 0.9})`);
        glow.addColorStop(0.4, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${p.opacity * 0.3})`);
        glow.addColorStop(1, `rgba(${GOLD_R},${GOLD_G},${GOLD_B},0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * 3.5, 0, Math.PI * 2);
        ctx.fillStyle = glow;
        ctx.fill();

        // Draw core dot
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${GOLD_R},${GOLD_G},${GOLD_B},${p.opacity})`;
        ctx.fill();
      });
    };

    let lastTime = performance.now();
    const loop = (now: number) => {
      const dt = Math.min(now - lastTime, 50); // cap at 50ms to avoid jump on tab focus
      lastTime = now;
      draw(dt);
      animId = requestAnimationFrame(loop);
    };
    animId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 2 }}
    />
  );
}
